import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import type {
    CollisionPolygonBounds,
    CollisionPolygonInfo,
    Nav2CollisionMonitorResponse,
} from "lib/nav2ParamsApi";
import { patchNav2CollisionMonitor } from "lib/nav2ParamsApi";
import { useToast } from "providers/ToastProvider";

type HandleEdge = "near" | "far" | "left" | "right";

type Props = {
    baseUrl: string;
    disabled?: boolean;
    collisionMonitor: Nav2CollisionMonitorResponse | null;
    onUpdated?: (res: Nav2CollisionMonitorResponse) => void;
};

const VIEW = { width: 320, height: 360, pad: 24 };
const X_MIN_CLAMP = 0;
const X_MAX_CLAMP = 2;
const Y_ABS_CLAMP = 1.5;

const DEFAULT_STOP: CollisionPolygonBounds = {
    x_min: 0.35,
    x_max: 0.55,
    y_min: -0.25,
    y_max: 0.25,
};
const DEFAULT_SLOW: CollisionPolygonBounds = {
    x_min: 0.3,
    x_max: 0.75,
    y_min: -0.35,
    y_max: 0.35,
};
const DEFAULT_FOOTPRINT: number[][] = [
    [0.24, 0.22],
    [0.24, -0.22],
    [-0.5, -0.22],
    [-0.5, 0.22],
];

function clampBounds(b: CollisionPolygonBounds): CollisionPolygonBounds {
    let x_min = Math.max(X_MIN_CLAMP, Math.min(X_MAX_CLAMP, b.x_min));
    let x_max = Math.max(X_MIN_CLAMP, Math.min(X_MAX_CLAMP, b.x_max));
    let y_min = Math.max(-Y_ABS_CLAMP, Math.min(Y_ABS_CLAMP, b.y_min));
    let y_max = Math.max(-Y_ABS_CLAMP, Math.min(Y_ABS_CLAMP, b.y_max));
    if (x_max <= x_min) x_max = Math.min(X_MAX_CLAMP, x_min + 0.05);
    if (y_max <= y_min) y_max = Math.min(Y_ABS_CLAMP, y_min + 0.05);
    return { x_min, x_max, y_min, y_max };
}

function ensureSlowContainsStop(
    stop: CollisionPolygonBounds,
    slow: CollisionPolygonBounds
): CollisionPolygonBounds {
    return clampBounds({
        x_min: Math.min(slow.x_min, stop.x_min),
        x_max: Math.max(slow.x_max, stop.x_max),
        y_min: Math.min(slow.y_min, stop.y_min),
        y_max: Math.max(slow.y_max, stop.y_max),
    });
}

function boundsEqual(a: CollisionPolygonBounds, b: CollisionPolygonBounds): boolean {
    return (
        a.x_min === b.x_min &&
        a.x_max === b.x_max &&
        a.y_min === b.y_min &&
        a.y_max === b.y_max
    );
}

function polygonInfoBounds(info?: CollisionPolygonInfo): CollisionPolygonBounds | null {
    if (!info?.bounds) return null;
    return clampBounds(info.bounds);
}

const CollisionPolygonEditor: React.FC<Props> = ({
    baseUrl,
    disabled = false,
    collisionMonitor,
    onUpdated,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const svgRef = useRef<SVGSVGElement | null>(null);
    const dragRef = useRef<{
        which: "stop" | "slow";
        edge: HandleEdge;
    } | null>(null);

    const initialStop = polygonInfoBounds(collisionMonitor?.polygons?.PolygonStop) ?? DEFAULT_STOP;
    const initialSlow =
        polygonInfoBounds(collisionMonitor?.polygons?.PolygonSlow) ?? DEFAULT_SLOW;
    const footprint = collisionMonitor?.footprint?.length
        ? collisionMonitor.footprint
        : DEFAULT_FOOTPRINT;

    const [stop, setStop] = useState<CollisionPolygonBounds>(initialStop);
    const [slow, setSlow] = useState<CollisionPolygonBounds>(
        ensureSlowContainsStop(initialStop, initialSlow)
    );
    const [savedStop, setSavedStop] = useState(initialStop);
    const [savedSlow, setSavedSlow] = useState(ensureSlowContainsStop(initialStop, initialSlow));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const nextStop =
            polygonInfoBounds(collisionMonitor?.polygons?.PolygonStop) ?? DEFAULT_STOP;
        const nextSlow = ensureSlowContainsStop(
            nextStop,
            polygonInfoBounds(collisionMonitor?.polygons?.PolygonSlow) ?? DEFAULT_SLOW
        );
        setStop(nextStop);
        setSlow(nextSlow);
        setSavedStop(nextStop);
        setSavedSlow(nextSlow);
    }, [collisionMonitor]);

    const dirty = !boundsEqual(stop, savedStop) || !boundsEqual(slow, savedSlow);

    const worldExtent = useMemo(() => {
        const xs = [
            stop.x_min,
            stop.x_max,
            slow.x_min,
            slow.x_max,
            ...footprint.map((p) => p[0]),
            0,
        ];
        const ys = [
            stop.y_min,
            stop.y_max,
            slow.y_min,
            slow.y_max,
            ...footprint.map((p) => p[1]),
            0,
        ];
        const xMin = Math.min(...xs, -0.2);
        const xMax = Math.max(...xs, 0.6);
        const yMin = Math.min(...ys, -0.4);
        const yMax = Math.max(...ys, 0.4);
        const xPad = Math.max(0.15, (xMax - xMin) * 0.15);
        const yPad = Math.max(0.15, (yMax - yMin) * 0.15);
        return {
            xMin: xMin - xPad,
            xMax: xMax + xPad,
            yMin: yMin - yPad,
            yMax: yMax + yPad,
        };
    }, [stop, slow, footprint]);

    const toSvg = useCallback(
        (x: number, y: number): { sx: number; sy: number } => {
            const { width, height, pad } = VIEW;
            const innerW = width - pad * 2;
            const innerH = height - pad * 2;
            const { xMin, xMax, yMin, yMax } = worldExtent;
            // +x forward → up; +y left → left
            const sx = pad + ((yMax - y) / (yMax - yMin)) * innerW;
            const sy = pad + ((xMax - x) / (xMax - xMin)) * innerH;
            return { sx, sy };
        },
        [worldExtent]
    );

    const fromSvg = useCallback(
        (sx: number, sy: number): { x: number; y: number } => {
            const { width, height, pad } = VIEW;
            const innerW = width - pad * 2;
            const innerH = height - pad * 2;
            const { xMin, xMax, yMin, yMax } = worldExtent;
            const y = yMax - ((sx - pad) / innerW) * (yMax - yMin);
            const x = xMax - ((sy - pad) / innerH) * (xMax - xMin);
            return { x, y };
        },
        [worldExtent]
    );

    const rectPoints = (b: CollisionPolygonBounds) => {
        const fl = toSvg(b.x_max, b.y_max);
        const fr = toSvg(b.x_max, b.y_min);
        const rr = toSvg(b.x_min, b.y_min);
        const rl = toSvg(b.x_min, b.y_max);
        return `${fl.sx},${fl.sy} ${fr.sx},${fr.sy} ${rr.sx},${rr.sy} ${rl.sx},${rl.sy}`;
    };

    const footprintPoints = footprint
        .map(([x, y]) => {
            const p = toSvg(x, y);
            return `${p.sx},${p.sy}`;
        })
        .join(" ");

    const edgeHandle = (b: CollisionPolygonBounds, edge: HandleEdge) => {
        const midX = (b.x_min + b.x_max) / 2;
        const midY = (b.y_min + b.y_max) / 2;
        if (edge === "far") return toSvg(b.x_max, midY);
        if (edge === "near") return toSvg(b.x_min, midY);
        if (edge === "left") return toSvg(midX, b.y_max);
        return toSvg(midX, b.y_min);
    };

    const applyEdgeDrag = (
        which: "stop" | "slow",
        edge: HandleEdge,
        worldX: number,
        worldY: number
    ) => {
        const updater = (prev: CollisionPolygonBounds): CollisionPolygonBounds => {
            const next = { ...prev };
            if (edge === "near") next.x_min = worldX;
            if (edge === "far") next.x_max = worldX;
            if (edge === "left") next.y_max = worldY;
            if (edge === "right") next.y_min = worldY;
            return clampBounds(next);
        };
        if (which === "stop") {
            setStop((prev) => {
                const nextStop = updater(prev);
                setSlow((s) => ensureSlowContainsStop(nextStop, s));
                return nextStop;
            });
        } else {
            setSlow((prev) => {
                const nextSlow = updater(prev);
                return ensureSlowContainsStop(stop, nextSlow);
            });
        }
    };

    const onPointerDown = (
        which: "stop" | "slow",
        edge: HandleEdge,
        e: React.PointerEvent
    ) => {
        if (disabled || saving) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { which, edge };
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current || !svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const sx = ((e.clientX - rect.left) / rect.width) * VIEW.width;
        const sy = ((e.clientY - rect.top) / rect.height) * VIEW.height;
        const { x, y } = fromSvg(sx, sy);
        applyEdgeDrag(dragRef.current.which, dragRef.current.edge, x, y);
    };

    const onPointerUp = () => {
        dragRef.current = null;
    };

    const setNumeric = (
        which: "stop" | "slow",
        key: keyof CollisionPolygonBounds,
        value: number
    ) => {
        if (Number.isNaN(value)) return;
        if (which === "stop") {
            setStop((prev) => {
                const nextStop = clampBounds({ ...prev, [key]: value });
                setSlow((s) => ensureSlowContainsStop(nextStop, s));
                return nextStop;
            });
        } else {
            setSlow((prev) => {
                const nextSlow = clampBounds({ ...prev, [key]: value });
                return ensureSlowContainsStop(stop, nextSlow);
            });
        }
    };

    const onSave = async () => {
        if (!baseUrl || saving || disabled || !dirty) return;
        setSaving(true);
        try {
            const nextStop = clampBounds(stop);
            const nextSlow = ensureSlowContainsStop(nextStop, clampBounds(slow));
            const res = await patchNav2CollisionMonitor(baseUrl, {
                polygons: {
                    PolygonStop: { bounds: nextStop },
                    PolygonSlow: { bounds: nextSlow },
                },
            });
            const savedS =
                polygonInfoBounds(res.polygons?.PolygonStop) ?? nextStop;
            const savedL = ensureSlowContainsStop(
                savedS,
                polygonInfoBounds(res.polygons?.PolygonSlow) ?? nextSlow
            );
            setStop(savedS);
            setSlow(savedL);
            setSavedStop(savedS);
            setSavedSlow(savedL);
            onUpdated?.(res);
            addToast({
                type: "success",
                message: t("navigation.polygonsSaved", {
                    defaultValue:
                        "Collision zones saved. Navigation is restarting.",
                }),
            });
        } catch (err) {
            addToast({
                type: "error",
                message:
                    err instanceof Error
                        ? err.message
                        : t("navigation.polygonsSaveFailed", {
                              defaultValue: "Could not save collision zones.",
                          }),
            });
        } finally {
            setSaving(false);
        }
    };

    const renderHandles = (which: "stop" | "slow", b: CollisionPolygonBounds, color: string) =>
        (["near", "far", "left", "right"] as HandleEdge[]).map((edge) => {
            const p = edgeHandle(b, edge);
            return (
                <circle
                    key={`${which}-${edge}`}
                    cx={p.sx}
                    cy={p.sy}
                    r={7}
                    fill={color}
                    stroke="#111"
                    strokeWidth={1.5}
                    className={disabled ? "cursor-not-allowed opacity-50" : "cursor-grab"}
                    onPointerDown={(e) => onPointerDown(which, edge, e)}
                />
            );
        });

    const origin = toSvg(0, 0);
    const xAxisTip = toSvg(Math.min(worldExtent.xMax - 0.05, 0.8), 0);
    const yAxisTip = toSvg(0, Math.min(worldExtent.yMax - 0.05, 0.5));

    return (
        <div className="mt-4 rounded-r5 border border-black70 bg-black90 p-3">
            <p className="text-h5 font-semibold text-white">
                {t("navigation.polygonsTitle", {
                    defaultValue: "Stop / slow zones",
                })}
            </p>
            <p className="mt-1 text-h5 text-black40">
                {t("navigation.polygonsHint", {
                    defaultValue:
                        "Top-down view in base_link (+x forward / up). Drag handles or edit numbers. Near edge should sit ahead of any payload on the lidar. Saving restarts navigation.",
                })}
            </p>

            <div className="mt-3 flex flex-wrap gap-4">
                <div className="shrink-0">
                    <svg
                        ref={svgRef}
                        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
                        width={VIEW.width}
                        height={VIEW.height}
                        className="rounded-r5 border border-black70 bg-black100 touch-none"
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                    >
                        <polygon
                            points={footprintPoints}
                            fill="rgba(148,163,184,0.35)"
                            stroke="#94a3b8"
                            strokeWidth={1.5}
                        />
                        <polygon
                            points={rectPoints(slow)}
                            fill="rgba(245,158,11,0.22)"
                            stroke="#f59e0b"
                            strokeWidth={2}
                        />
                        <polygon
                            points={rectPoints(stop)}
                            fill="rgba(239,68,68,0.28)"
                            stroke="#ef4444"
                            strokeWidth={2}
                        />
                        <line
                            x1={origin.sx}
                            y1={origin.sy}
                            x2={xAxisTip.sx}
                            y2={xAxisTip.sy}
                            stroke="#64748b"
                            strokeWidth={1.5}
                        />
                        <line
                            x1={origin.sx}
                            y1={origin.sy}
                            x2={yAxisTip.sx}
                            y2={yAxisTip.sy}
                            stroke="#64748b"
                            strokeWidth={1.5}
                        />
                        <text
                            x={xAxisTip.sx}
                            y={xAxisTip.sy - 8}
                            fill="#94a3b8"
                            fontSize={11}
                            textAnchor="middle"
                        >
                            +x
                        </text>
                        <text
                            x={yAxisTip.sx - 10}
                            y={yAxisTip.sy + 4}
                            fill="#94a3b8"
                            fontSize={11}
                            textAnchor="middle"
                        >
                            +y
                        </text>
                        <circle cx={origin.sx} cy={origin.sy} r={3} fill="#e2e8f0" />
                        {renderHandles("slow", slow, "#f59e0b")}
                        {renderHandles("stop", stop, "#ef4444")}
                    </svg>
                    <div className="mt-2 flex flex-wrap gap-3 text-h5 text-black40">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" />
                            {t("navigation.polygonStop", { defaultValue: "Stop" })}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500" />
                            {t("navigation.polygonSlow", { defaultValue: "Slow" })}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-400" />
                            {t("navigation.polygonFootprint", {
                                defaultValue: "Footprint",
                            })}
                        </span>
                    </div>
                </div>

                <div className="min-w-[220px] flex-1 space-y-4">
                    <BoundsFields
                        title={t("navigation.polygonStop", { defaultValue: "Stop" })}
                        bounds={stop}
                        disabled={disabled || saving}
                        onChange={(key, value) => setNumeric("stop", key, value)}
                    />
                    <BoundsFields
                        title={t("navigation.polygonSlow", { defaultValue: "Slow" })}
                        bounds={slow}
                        disabled={disabled || saving}
                        onChange={(key, value) => setNumeric("slow", key, value)}
                    />
                    <FunctionalButton
                        title={
                            saving
                                ? t("common:saving")
                                : t("navigation.savePolygons", {
                                      defaultValue: "Save zones",
                                  })
                        }
                        type="button"
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                        disabled={disabled || saving || !dirty}
                        onClick={() => void onSave()}
                    />
                </div>
            </div>
        </div>
    );
};

function BoundsFields({
    title,
    bounds,
    disabled,
    onChange,
}: {
    title: string;
    bounds: CollisionPolygonBounds;
    disabled: boolean;
    onChange: (key: keyof CollisionPolygonBounds, value: number) => void;
}) {
    const { t } = useTranslation("settings");
    const fields: { key: keyof CollisionPolygonBounds; label: string }[] = [
        {
            key: "x_min",
            label: t("navigation.polygonNear", { defaultValue: "Near x (m)" }),
        },
        {
            key: "x_max",
            label: t("navigation.polygonFar", { defaultValue: "Far x (m)" }),
        },
        {
            key: "y_max",
            label: t("navigation.polygonLeft", { defaultValue: "Left y (m)" }),
        },
        {
            key: "y_min",
            label: t("navigation.polygonRight", { defaultValue: "Right y (m)" }),
        },
    ];

    return (
        <div>
            <p className="mb-2 text-h5 font-semibold text-white">{title}</p>
            <div className="grid grid-cols-2 gap-2">
                {fields.map(({ key, label }) => (
                    <label key={key} className="flex flex-col gap-1">
                        <span className="text-h5 text-black40">{label}</span>
                        <input
                            type="number"
                            step={0.01}
                            value={Number(bounds[key].toFixed(3))}
                            disabled={disabled}
                            onChange={(e) => onChange(key, Number(e.target.value))}
                            className="rounded-r5 border border-black70 bg-black100 px-2 py-1.5 text-h5 text-white disabled:opacity-50"
                        />
                    </label>
                ))}
            </div>
        </div>
    );
}

export default CollisionPolygonEditor;
